import { SchoolZone, SchoolZoneSmall } from '@ice-web-app/assets';
import { ContentZone, DsIcon, DsTypography, SchoolMenu } from '@ice-web-app/designSystem';
import { TIPS, ZONES_BASE_PATH } from '@ice-web-app/shared-constants';
import { usePopover, useTranslation } from '@ice-web-app/shared-hooks';
import { useCurrentUserStore, useZoneStore } from '@ice-web-app/shared-store';
import { getCurrentZoneBasePath } from '@ice-web-app/shared-utils';
import { Box, Stack } from '@mui/material';
import { MouseEventHandler, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const ZoneItem = ({
  disabled,
  isActive,
  title,
  icon,
  subTitle,
  onClick,
}: {
  disabled: boolean;
  isActive: boolean;
  title: string;
  icon: React.ReactNode;
  subTitle?: string;
  onClick?: () => void;
}) => {
  return (
    <Stack
      alignItems="center"
      gap={{ xs: 0, lg: 0.5 }}
      onClick={onClick}
      sx={{
        minWidth: { xs: 95, lg: 109 },
        ...((disabled || !isActive) && { opacity: '0.5' }),
        ...(!disabled && {
          '&:hover': { opacity: '1', transition: 'opacity 0.3s ease-in' },
        }),
      }}
    >
      {icon}
      <DsTypography variant="subtitle2" textAlign="center" sx={{ whiteSpace: 'nowrap' }}>
        {title}
      </DsTypography>
      {subTitle && (
        <DsTypography variant="subtitle2" textAlign="center" sx={{ mt: -1.5 }}>
          {subTitle}
        </DsTypography>
      )}
    </Stack>
  );
};

const ZoneItemWrapper = ({
  children,
  path,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  path?: string;
  disabled: boolean;
  onClick?: MouseEventHandler<any>;
}) => {
  return path ? (
    <Link
      to={path}
      onClick={onClick}
      style={{
        ...(!disabled ? { cursor: 'pointer' } : { pointerEvents: 'none' }),
      }}
    >
      {children}
    </Link>
  ) : (
    <Box onClick={onClick} component="button" disabled={disabled}>
      {children}
    </Box>
  );
};

const ZoneMenu = () => {
  const location = useLocation();
  const schoolMenuPopover = usePopover();
  const { setCurrentZone } = useCurrentUserStore();

  const {
    schoolZone,
    contentZone,
    communicationZone,
    managementInformationZone,
    workflowZone,
    satV5Zone,
    timeTableZone,
    challengeZone,
    contactZone,
    commsZone,
    satModuleZone,
    setSchoolZone,
    setContentZone,
    setCommunicationZone,
    setManagementInformationZone,
    setWorkflowZone,
    setSatV5Zone,
    setTimeTableZone,
    setChallengeZone,
    setContactZone,
    setCommsZone,
    setSatModuleZone,
  } = useZoneStore();

  const menuItems = [
    schoolZone,
    contactZone,
    contentZone,
    commsZone,
    communicationZone,
    workflowZone,
    managementInformationZone,
    timeTableZone,
    challengeZone,
  ];

  useEffect(() => {
    if (schoolZone?.subTitle) {
      setSchoolZone({
        isActive: true,
        icon: <SchoolZoneSmall />,
        onClick: (e) => {
          schoolMenuPopover.onOpen(e);
        },
      });
      setCurrentZone({ name: 'schoolZone' });
    } else if (schoolZone) {
      setSchoolZone({
        isActive: false,
        icon: <SchoolZone />,
        onClick: (e) => {
          schoolMenuPopover.onOpen(e);
        },
      });
    }

    if (schoolMenuPopover?.open) {
      setSchoolZone({
        isActive: true,
      });
    }
  }, [schoolZone?.subTitle, schoolZone?.isVisible, schoolMenuPopover?.open]);

  useEffect(() => {
    const zoneBasePath = getCurrentZoneBasePath(location.pathname);

    if (schoolZone && zoneBasePath === ZONES_BASE_PATH.defaultSchoolZone) {
      setSchoolZone({
        isActive: true,
      });
    } else if (schoolZone) {
      setSchoolZone({
        isActive: false,
        icon: <SchoolZone />,
        subTitle: undefined,
      });
    }

    if (contentZone && zoneBasePath === ZONES_BASE_PATH.content) {
      setContentZone({
        isActive: true,
      });
    } else if (contentZone) {
      setContentZone({
        isActive: false,
      });
    }

    if (communicationZone && zoneBasePath === ZONES_BASE_PATH.communication) {
      setCommunicationZone({
        isActive: true,
      });
    } else if (communicationZone) {
      setCommunicationZone({
        isActive: false,
      });
    }

    if (managementInformationZone && zoneBasePath === ZONES_BASE_PATH.managementInformation) {
      setManagementInformationZone({
        isActive: true,
      });
    } else if (managementInformationZone) {
      setManagementInformationZone({
        isActive: false,
      });
    }

    if (workflowZone && zoneBasePath === ZONES_BASE_PATH.workflow) {
      setWorkflowZone({
        isActive: true,
      });
    } else if (workflowZone) {
      setWorkflowZone({
        isActive: false,
      });
    }

    if (timeTableZone && zoneBasePath === ZONES_BASE_PATH.timeTable) {
      setTimeTableZone({
        isActive: true,
      });
    } else if (timeTableZone) {
      setTimeTableZone({
        isActive: false,
      });
    }

    if (challengeZone && zoneBasePath === ZONES_BASE_PATH.challenge) {
      setChallengeZone({ isActive: true });
    } else if (challengeZone) {
      setChallengeZone({
        isActive: false,
      });
    }

    if (contactZone && zoneBasePath === ZONES_BASE_PATH.contact) {
      setContactZone({
        isActive: true,
      });
    } else if (contactZone) {
      setContactZone({
        isActive: false,
      });
    }

    if (commsZone && zoneBasePath === ZONES_BASE_PATH.comms) {
      setCommsZone({
        isActive: true,
      });
    } else if (commsZone) {
      setCommsZone({
        isActive: false,
      });
    }

    if (satV5Zone && zoneBasePath === ZONES_BASE_PATH.satV5) {
      setSatV5Zone({
        isActive: true,
      });
    } else if (satV5Zone) {
      setSatV5Zone({
        isActive: false,
      });
    }

    if (satModuleZone && zoneBasePath === ZONES_BASE_PATH.satModule) {
      setSatModuleZone({
        isActive: true,
      });
    } else if (satModuleZone) {
      setSatModuleZone({
        isActive: false,
      });
    }
  }, [
    location,
    timeTableZone?.isActive,
    challengeZone?.isActive,
    contactZone?.isActive,
    commsZone?.isActive,
    satV5Zone?.isActive,
    satModuleZone?.isActive,
    managementInformationZone?.isActive,
    workflowZone?.isActive,
    communicationZone?.isActive,
    contentZone?.isActive,
  ]);
  return (
    <>
      <Stack
        direction="row"
        justifyContent="center"
        marginRight="auto"
        gap={schoolZone ? 2 : 4}
        data-tour={TIPS.LAYOUT.HEADER.ZONE_MENU.DATA}
      >
        <>
          <ZoneItemWrapper onClick={() => {}} disabled={false} path={'/sat-v5-zone'}>
            <ZoneItem disabled={false} isActive={true} title="SAT V5 Zone" icon={<SchoolZone />} />
          </ZoneItemWrapper>
          <ZoneItemWrapper onClick={() => {}} disabled={false} path={'/sat-module'}>
            <ZoneItem disabled={false} isActive={true} title="SAT Module" icon={<SchoolZone />} />
          </ZoneItemWrapper>
          {menuItems.map((item, index) => {
            return item ? (
              <ZoneItemWrapper key={index} onClick={item.onClick} disabled={item.disabled} path={item.path}>
                <ZoneItem
                  disabled={item.disabled}
                  isActive={item.isActive}
                  title={item.title}
                  icon={item.icon}
                  subTitle={item.subTitle}
                />
              </ZoneItemWrapper>
            ) : null;
          })}
        </>
      </Stack>
      <SchoolMenu popover={schoolMenuPopover} />
    </>
  );
};

export default ZoneMenu;
